import { CurrencyPipe, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import { LOCALE_ID, NgModule } from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter, MomentDateModule } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateModule } from '@ngx-translate/core';
import { AUTHENTICATION_SERVICE } from '@shared/injection-tokens/authentication-service';
import { BaseEnvironment, ENVIRONMENT } from '@shared/injection-tokens/environment';
import { HTTP_SERVICE } from '@shared/injection-tokens/http-service';
import { USERS_SERVICE } from '@shared/injection-tokens/users-service';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import { ToastrModule } from 'ngx-toastr';
import { SharedModule } from 'projects/shared/src/lib/shared';

import {
  ConfigurableTableDialogModule,
} from '../../../shared/src/lib/shared/components/configurable-table-dialog/configurable-table-dialog.module';
import { I18nService } from '../../../shared/src/lib/shared/services/local/i18n.service';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthenticationService } from './core/authentication/authentication.service';
import { CoreModule } from './core/core.module';
import { HttpService } from './core/http/http.service';
import { UsersService } from './shared/services/users.service';

registerLocaleData(localeDe);
registerLocaleData(localeEn);

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY'
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

const maskConfig: Partial<IConfig> = {
  validation: true,
};

export function getCurrentLanguage(i18nService: I18nService) {
  i18nService.init(environment.supportedLanguages);
  return i18nService.language;
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    NgxMaskModule.forRoot(maskConfig),
    CoreModule,
    SharedModule,
    TranslateModule.forRoot(),
    OAuthModule.forRoot(),
    MomentDateModule,
    MatDatepickerModule,
    ConfigurableTableDialogModule,
    ToastrModule.forRoot({
      timeOut: 5000,
      positionClass: 'toast-top-right',
      preventDuplicates: true
    }),
    AppRoutingModule // must be imported as the last module as it contains the fallback route
  ],
  providers: [
    // The OAuth tokens are kept in localStorage (the library defaults to sessionStorage, which is lost in new tabs),
    // so a reload refreshes the session silently instead of running through the login redirect
    { provide: OAuthStorage, useFactory: () => localStorage },
    CurrencyPipe,
    {
      provide: USERS_SERVICE, useClass: UsersService
    },
    {
      provide: AUTHENTICATION_SERVICE, useClass: AuthenticationService
    },
    {
      provide: HTTP_SERVICE, useClass: HttpService
    },
    {
      provide: ENVIRONMENT,
      useValue: {
        applicationName: 'website',
        defaultLanguage: environment.defaultLanguage,
        idpServerUrl: environment.idpServerUrl,
        loginClientId: environment.loginClientId,
        serverUrl: environment.cmsServerUrl,
        supportedLanguages: environment.supportedLanguages
      } as BaseEnvironment
    },
    { provide: LOCALE_ID, useFactory: getCurrentLanguage, deps: [I18nService] },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    provideHttpClient(withXhr(), withInterceptorsFromDi())
  ]
})
export class AppModule {

  constructor() {
    // https://patorjk.com/software/taag/#p=display&f=Epic&t=Mindpop
    console.log(`
 _______ _________ _        ______   _______  _______  _______ 
(       )\\__   __/( (    /|(  __  \\ (  ____ )(  ___  )(  ____ )
| () () |   ) (   |  \\  ( || (  \\  )| (    )|| (   ) || (    )|
| || || |   | |   |   \\ | || |   ) || (____)|| |   | || (____)|
| |(_)| |   | |   | (\\ \\) || |   | ||  _____)| |   | ||  _____)
| |   | |   | |   | | \\   || |   ) || (      | |   | || (      
| )   ( |___) (___| )  \\  || (__/  )| )      | (___) || )      
|/     \\|\\_______/|/    )_)(______/ |/       (_______)|/       
                                                               

    `);
    console.log('Hi! Nice to meet you. Interested in any developer related product, such as our api? Contact us at support@mindpopapp.com!');
    console.log(' ');
  }
}
